import { Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { IPTC } from "./IPTC";
import { DOMAINS, NrkDataSchema, RightsMarkerSchema } from "./nrk-data";
import { NullableSchema } from "./type-util";
import { PublicationSchema } from "./publications";
export var Mimir;
(function (Mimir) {
    let Form;
    (function (Form) {
        /** Editable fields not in IPTC */
        Form.CommonFieldsSchema = Type.Object({
            /** Id for derivate generation (Kaleido Original ID), CDN, etc */
            publicId: Type.String({ description: "Id for derivate generation (Kaleido ID)" }),
            /**Status of publication process (transfer to CDN, etc) */
            publicAvailabilityStatus: Type.String({
                description: "Status of publication process (transfer to CDN, etc)",
            }),
            /** The system the image was uploaded from */
            sourceSystem: Type.String({ description: "The system the image was uploaded from" }),
            /**
             * Top level characteristics of content
             *
             * @link https://authority.nrk.no/datadictionary/category
             */
            category: Type.Array(Type.String({
                description: "Top level characteristics of content",
                $id: "https://authority.nrk.no/datadictionary/category",
                title: "Category",
            })),
            /**
             * Keyword or term assigned to an object by an automatic process
             *
             * @link https://authority.nrk.no/datadictionary/autotags
             */
            autoTags: Type.Array(Type.String({
                description: "Keyword or term assigned to an object by an automatic process",
                $id: "https://authority.nrk.no/datadictionary/autotags",
                title: "Auto tags",
            })),
            /**
             * The presence of this field indicates that the item has sensitive content,
             * and it's value gives an explanation
             *
             * @link  "https://www.ebu.ch/metadata/ontologies/ebucore/ebucore_ExploitationIssues.html"
             */
            sensitiveContentNotice: Type.String({
                description: "The presence of this field indicates that the item has sensitive content, and it's value gives an explanation",
                title: "Sensitive content notice",
            }),
            /**
             * Classification of content according to the purpose for which it is to be used.
             *
             * @link https://authority.nrk.no/datadictionary/usageDomain
             */
            usageDomain: Type.Array(Type.Union([
                Type.Literal(DOMAINS.promotional, {
                    description: "A still image used to advertise NRK's TV, radio and podcast content.",
                    title: "Promotional",
                    $id: DOMAINS.promotional,
                }),
                Type.Literal(DOMAINS.editorial, {
                    description: "A still image produced on an editorial assignment.",
                    title: "Editorial",
                    $id: DOMAINS.editorial,
                }),
                Type.Literal(DOMAINS.internal, {
                    description: "Images from social gatherings; historical photos of the offices; internal lectures; employee photos, etc. ",
                    title: "Internal",
                    $id: DOMAINS.internal,
                }),
            ]), {
                description: "Usage domain",
                $id: "https://authority.nrk.no/datadictionary/usageDomain",
                title: "Usage Domain",
            }),
        });
        Form.BildebankenFieldsSchema = Type.Object({
            personInImageIds: Type.Array(Type.String(), {
                description: "Authority IDs for people in image, should be aligned with IPTC field personInImage",
            }),
            rightsMarker: RightsMarkerSchema,
            data: NrkDataSchema,
            publications: Type.Array(PublicationSchema),
        }, { description: "Editable fields not in IPTC" });
        Form.ReadonlyFieldsSchema = Type.Object({
            createdOn: Type.String({ description: "When file is uploaded" }),
        });
        Form.EditableFieldsSchema = Type.Composite([IPTC.FieldsSchema, Form.CommonFieldsSchema, Form.BildebankenFieldsSchema], {
            description: "All fields that can be edited",
        });
        Form.AllFieldsSchema = Type.Composite([Form.EditableFieldsSchema, Form.ReadonlyFieldsSchema], {
            description: "All form fields",
        });
    })(Form = Mimir.Form || (Mimir.Form = {}));
    Mimir.DetectedLabelsSchema = Type.Array(Type.Object({
        Instances: Type.Array(Type.String()),
        Parents: Type.Array(Type.Object({ Name: Type.String() })),
        maxConfidence: Type.Number(),
        name: Type.String(),
    }), { description: "Labels detected by Mimir AI services" });
    Mimir.AssetVisibilitySchema = Type.Object({
        level: Type.Union([
            Type.Literal("selected_groups"),
            Type.Literal("tenant"),
            Type.Literal("owner"),
        ]),
        groups: Type.Optional(Type.Array(Type.String())),
        users: Type.Optional(Type.Array(Type.String())),
    });
    Mimir.TechnicalMetadataSchema = Type.Intersect([
        Type.Object({
            technical_image_height: Type.Number(),
            technical_image_width: Type.Number(),
            /** "JPEG", "NEF", etc */
            technical_image_file_type: Type.String(),
            image_exif_ISO: Type.Optional(Type.Number()),
            image_exif_ExposureCompensation: Type.Optional(Type.String()),
            /** F-stop */
            image_exif_FNumber: Type.Optional(Type.Number()),
            /** "24 mm", etc */
            image_exif_FocalLengthIn35mmFormat: Type.Optional(Type.String()),
            /** "1/25", etc */
            image_exif_ShutterSpeed: Type.Optional(Type.String()),
            /** "2022:10:20 18:12:04" (Without time zone) */
            image_exif_CreateDate: Type.Optional(Type.String()),
            /** "FUJIFILM", "Apple", etc */
            image_exif_LensMake: Type.Optional(Type.String()),
            /** "FUJIFILM", "Apple", etc */
            image_exif_Make: Type.Optional(Type.String()),
            /** "X-E1", "iPhone 13", etc */
            image_exif_Model: Type.Optional(Type.String()),
            /* "Sony FE 24-70mm F2.8", "" */
            image_exif_Lens: Type.Optional(Type.String()),
            /** "FE 24-70mm F2.8 GM", "XF27mmF2.8", "iPhone 13 back dual wide camera 5.1mm f/1.6" etc */
            image_exif_LensModel: Type.Optional(Type.String({ examples: ["XF27mmF2.8", "iPhone 13 back dual wide camera 5.1mm f/1.6"] })),
            image_exif_ColorType: Type.Optional(Type.String()),
            /** "RGB", "RGB with alpha" */
            image_exif_Artist: Type.Optional(Type.String()),
            image_exif_GPSLatitude: Type.Optional(Type.String()),
            image_exif_GPSLongitude: Type.Optional(Type.String()),
            image_exif_GPSHPositioningError: Type.Optional(Type.String()),
            image_exif_GPSAltitude: Type.Optional(Type.String()),
            image_exif_GPSImgDirection: Type.Optional(Type.String()),
            image_exif_GPSSpeed: Type.Optional(Type.String()),
            image_exif_Flash: Type.Optional(Type.String()),
            image_exif_Orientation: Type.Optional(Type.String()),
            image_exif_SerialNumber: Type.Optional(Type.String()),
            image_exif_SubSecDateTimeOriginal: Type.Optional(Type.String()),
            image_exif_Keywords: Type.Optional(Type.String()),
            image_exif_LensInfo: Type.Optional(Type.String()),
        }),
        Type.Record(Type.String(), Type.Union([Type.String(), Type.Number()])),
    ], { description: "Technical metadata from original file" });
    const ItemStateSchema = Type.Union([
        Type.Literal("error"),
        Type.Literal("progressing"),
        Type.Literal("complete"),
        Type.Literal("deleting"),
        Type.String(),
    ]);
    const LabelDetectionStateSchema = Type.Union([
        Type.Literal("progressing"),
        Type.Literal("complete"),
        Type.Literal("error"),
        Type.String(),
    ]);
    //type TranscodingState = "error" | "progressing" | "complete";
    const SimplifiedFormData = Type.Partial(Type.Pick(Form.AllFieldsSchema, [
        "title",
        "description",
        "copyrightNotice",
        "copyrightOwners",
        "rightsMarker",
        "publicId",
        "usageTerms",
        "creators",
        "creditLine",
        "mediaCreatedOn",
        "createdOn",
    ]));
    /**
     * Data common between individual item requests and search results
     * Equivalent to BaseItemSchema, but with only a subset of metadata.formData fields
     * */
    const SimplifiedBaseItemSchema = Type.Object({
        id: Type.String(),
        metadata: Type.Object({
            formId: Type.String(),
            formData: Type.Optional(SimplifiedFormData),
        }),
        thumbnail: Type.Optional(NullableSchema(Type.String())),
        proxy: Type.Optional(NullableSchema(Type.String())),
        itemState: ItemStateSchema,
        createdBy: Type.String({ description: "User ID" }),
        visibleTo: Type.Optional(Mimir.AssetVisibilitySchema),
        modifiedOn: Type.String(),
        detectedLabels: Type.Optional(Mimir.DetectedLabelsSchema),
        folderParents: Type.Optional(NullableSchema(Type.Array(Type.String()))),
        mediaType: Type.String({ description: "MIME type, e.g. image/jpeg" }),
        mediaSize: Type.Optional(Type.Number()),
        proxySize: Type.Optional(Type.Number()),
        originalFileName: Type.String(),
    });
    const BaseFormData = Type.Partial(Form.AllFieldsSchema);
    /**
     * Data common between individual item requests and search results
     * */
    const BaseItemSchema = Type.Object({
        id: Type.String(),
        metadata: Type.Object({
            formId: Type.String(),
            formData: BaseFormData,
        }),
        thumbnail: Type.Optional(NullableSchema(Type.String())),
        proxy: Type.Optional(NullableSchema(Type.String())),
        itemState: ItemStateSchema,
        createdBy: Type.String({ description: "User ID" }),
        visibleTo: Type.Optional(Mimir.AssetVisibilitySchema),
        modifiedOn: Type.String(),
        detectedLabels: Type.Optional(Mimir.DetectedLabelsSchema),
        labelDetectionState: Type.Optional(LabelDetectionStateSchema),
        folderParents: Type.Optional(NullableSchema(Type.Array(Type.String()))),
        mediaType: Type.String({ description: "MIME type, e.g. image/jpeg" }),
        mediaSize: Type.Optional(Type.Number()),
        proxySize: Type.Optional(Type.Number()),
        originalFileName: Type.String(),
    });
    Mimir.SearchResultItemSchema = BaseItemSchema;
    Mimir.SimplifiedSearchResultItemSchema = SimplifiedBaseItemSchema;
    Mimir.RelationSchema = Type.Object({
        type: Type.String(),
        item: Mimir.SearchResultItemSchema,
    });
    Mimir.FullItemMetadataSchema = Type.Composite([
        BaseItemSchema,
        Type.Object({
            title: Type.Optional(Type.String()),
            highRes: Type.Optional(Type.String()),
            metadataVersion: Type.Number(),
            technicalMetadata: Type.Optional(Type.Object({
                formId: Type.String(),
                formData: Mimir.TechnicalMetadataSchema,
            })),
            lastModifiedBy: Type.Optional(Type.String()),
            replacementOf: Type.Optional(Type.String()),
            replacedBy: Type.Optional(Type.String()),
            isDeleting: Type.Optional(Type.Boolean()),
            hasRelations: Type.Optional(Type.Boolean()),
            // some properties have been omitted
        }),
    ]);
    const ItemTypeSchema = Type.Union([
        Type.Literal("image"),
        Type.Literal("folder"),
        Type.Literal("file"),
        Type.Literal("person"),
        Type.Literal("video"),
        Type.Literal("streaming"),
        Type.String(),
    ]);
    const TypeCountSchema = Type.Array(Type.Object({
        key: ItemTypeSchema,
        doc_count: Type.Number(),
    }));
    const CreateSearchResponseSchema = (itemSchema) => Type.Object({
        typeCount: Type.Optional(TypeCountSchema),
        total: Type.Optional(Type.Number()),
        count: Type.Number(),
        from: Type.Number(),
        _embedded: Type.Object({
            collection: Type.Array(itemSchema),
        }),
    });
    Mimir.SearchResponseSchema = CreateSearchResponseSchema(Mimir.SearchResultItemSchema);
    Mimir.SimplifiedSearchResponseSchema = CreateSearchResponseSchema(Mimir.SimplifiedSearchResultItemSchema);
    Mimir.SimplifiedFolderSearchResultItemSchema = Type.Intersect([
        Type.Omit(Mimir.SimplifiedSearchResultItemSchema, [
            "metadata",
            "itemState",
            "createdBy",
            "modifiedOn",
            "mediaType",
            "originalFileName",
        ]),
        Type.Object({
            name: Type.String(),
            owner: Type.String(),
            metadata: Type.Optional(Type.Object({
                formId: Type.String(),
                formData: Type.Optional(Type.Object({
                    displayName: Type.Optional(Type.String()),
                })),
            })),
        }),
    ]);
    Mimir.SimplifiedFolderSearchResponseSchema = CreateSearchResponseSchema(Mimir.SimplifiedFolderSearchResultItemSchema);
    Mimir.SortablePropertySchema = Type.Union([
        Type.Literal("media_created_date"),
        Type.Literal("item_created_date"),
        Type.Literal("modifiedOn"),
    ], {
        default: "media_created_date",
        description: "Properties that search results can be sorted by",
        examples: ["media_created_date", "item_created_date", "modifiedOn"],
    });
    function isSortableProperty(value) {
        return Value.Check(Mimir.SortablePropertySchema, value);
    }
    Mimir.isSortableProperty = isSortableProperty;
    Mimir.SortOrderSchema = Type.Union([Type.Literal("asc"), Type.Literal("desc")], {
        default: "desc",
        examples: ["asc", "desc"],
    });
    function isSortOrder(value) {
        return Value.Check(Mimir.SortOrderSchema, value);
    }
    Mimir.isSortOrder = isSortOrder;
    function getMediaCreatedOn(item) {
        var _a, _b;
        if ((_b = (_a = item.metadata) === null || _a === void 0 ? void 0 : _a.formData) === null || _b === void 0 ? void 0 : _b.mediaCreatedOn) {
            return new Date(item.metadata.formData.mediaCreatedOn);
        }
        else {
            return null;
        }
    }
    Mimir.getMediaCreatedOn = getMediaCreatedOn;
    function getCreatedOn(item) {
        var _a, _b;
        if ((_b = (_a = item.metadata) === null || _a === void 0 ? void 0 : _a.formData) === null || _b === void 0 ? void 0 : _b.createdOn) {
            return new Date(item.metadata.formData.createdOn);
        }
        else {
            return null;
        }
    }
    Mimir.getCreatedOn = getCreatedOn;
    function getTitle(item) {
        return item.metadata.formData.title || item.originalFileName;
    }
    Mimir.getTitle = getTitle;
    Mimir.UserSchema = Type.Object({
        id: Type.String({ description: "User Id in Mimir" }),
        familyName: Type.String(),
        givenName: Type.String(),
        email: Type.String(),
    });
    //////     Folders
    const FolderTypeSchema = Type.Union([
        Type.Literal("tenant-root"),
        Type.Literal("archive-root"),
        Type.String(),
    ]);
    Mimir.CreateFolderPayloadSchema = Type.Object({
        name: Type.String(),
        parent: Type.String(),
        visibleTo: Type.Optional(Mimir.AssetVisibilitySchema),
    });
    Mimir.UpdateFolderPayloadSchema = Type.Partial(Type.Object({
        name: Type.String(),
        parentId: Type.String(),
        visibleTo: Mimir.AssetVisibilitySchema,
        updatableBy: Mimir.AssetVisibilitySchema,
    }));
    Mimir.UpdateFolderOptionsSchema = Type.Object({
        noRecursiveUpdate: Type.Optional(Type.Boolean({ description: "Avoid updating visibility of nested contents", default: false })),
        implicitVisibilityUpdate: Type.Optional(Type.Boolean({
            description: "Inherit the visibility of the destination folder after move",
            default: true,
        })),
    });
    Mimir.FolderContentOptionsSchema = Type.Object({
        type: Type.Optional(Type.Union([Type.Literal("folder"), Type.Literal("item")])),
        fetchAllRecursively: Type.Optional(Type.Boolean({
            default: false,
            description: "Recursively fetches all descendants (folders and items) of the requested folder",
        })),
        scroll: Type.Optional(Type.Boolean()),
        mScrollId: Type.Optional(Type.String()),
        skipFolders: Type.Optional(Type.Boolean({ default: false })),
        readableMetadataFields: Type.Optional(Type.Boolean({ default: false })),
    });
    const FolderMetadata = Type.Partial(Type.Object({
        displayName: Type.String(),
    }));
    Mimir.FolderMetadataUpdateResponseSchema = Type.Object({
        id: Type.String(),
        metadata: Type.Object({ formId: Type.String(), formData: FolderMetadata }),
    });
    Mimir.FolderSchema = Type.Object({
        id: Type.String({ description: "ID of the folder" }),
        name: Type.String({ description: "Name of the folder" }),
        owner: Type.String({ description: "ID of owner" }),
        createdOn: Type.String({ description: "Folder creation date" }),
        hasSubfolder: Type.Boolean(),
        kind: Type.Optional(FolderTypeSchema),
        metadata: Type.Optional(Type.Object({ formId: Type.String(), formData: Type.Optional(FolderMetadata) })),
        // Properties not documented in REST API
        itemType: Type.Literal("folder"),
        parent: Type.Optional(Type.String()),
        visibleTo: Type.Optional(Mimir.AssetVisibilitySchema),
    });
    Mimir.FolderContentHitResponseSchema = Type.Intersect([
        Type.Union([Mimir.FolderSchema, BaseItemSchema]),
        Type.Object({ itemType: ItemTypeSchema }),
        Type.Object({ thumbnail: Type.Optional(NullableSchema(Type.String())) }),
    ]);
    Mimir.FolderContentResponseSchema = Type.Object({
        from: Type.Optional(Type.Number()),
        total: Type.Optional(Type.Number()),
        mScrollId: Type.Optional(Type.String()),
        hits: Type.Array(Mimir.FolderContentHitResponseSchema),
    });
    Mimir.AncestryResponseSchema = Type.Record(Type.String(), Type.Object({
        paths: Type.Record(Type.String(), Type.Array(Type.Array(Type.Object({
            id: Type.String(),
            name: Type.String(),
        })))),
    }));
    Mimir.PathResponseSchema = Type.Array(Type.Record(Type.String(), Type.String()));
    const SortableFolderPropertySchema = Type.Union([Type.Literal("title"), Type.Literal("item_created_date")], {
        description: "Properties that folder search results can be sorted by",
        examples: ["title", "item_created_date"],
    });
    Mimir.FolderSortSchema = Type.Object({
        sortProperty: SortableFolderPropertySchema,
        sortOrder: Mimir.SortOrderSchema,
    });
})(Mimir || (Mimir = {}));
//# sourceMappingURL=mimir.js.map